import useAxiosInterceptor from 'src/services/interceptor';
import { therapyPlanningPaths } from '../constants';

class PatientScreeningService {
  private axiosInstance;

  constructor() {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- factory, not a React hook (matches npcLogService)
    this.axiosInstance = useAxiosInterceptor();
  }

  getTherapyPlanning = (payload: any) => {
    return this.axiosInstance.get(
      `${therapyPlanningPaths.therapyPlanning}v2/${payload?.patientId}`,
      {
        params: payload?.params,
        cancelToken: payload?.abortToken,
      }
    );
  };
  getIsTherapyPlanningPatient = (payload: any) => {
    return this.axiosInstance.get(
      `${therapyPlanningPaths.therapyPlanning}eligible-for-planning/${payload?.patientId}`
    );
  };

  checkTherapyPlanningPatient = (payload: any) => {
    return this.axiosInstance.get(
      `${therapyPlanningPaths.therapyPlanning}future-plan-minutes/${payload?.patientId}?anticipatedDischargeDate=${payload?.dischargeDate}&discipline=${payload?.type}`
    );
  };

  checkTherapyPlanningPatient_v2 = (payload: any) => {
    return this.axiosInstance.get(
      `${therapyPlanningPaths.therapyPlanningV2}future-plan-minutes/${payload?.patientId}?anticipatedDischargeDate=${payload?.dischargeDate}&discipline=${payload?.type}`
    );
  };

  getDateOfService = (payload: any) => {
    return this.axiosInstance.get(`provider/patient/latest-dos/${payload?.patientId}`);
  };
  // `date` (YYYY-MM-DD, optional) picks the payer effective on that date — the MDS form sends
  // the ARD so the template follows the payer in effect on the assessment date.
  getCaseMixGroup = (payload: any) => {
    return this.axiosInstance.get(`provider/pdpm-case-mix-template/patient-case-mix-minutes`, {
      params: { patientId: payload?.patientId, date: payload?.date || undefined },
    });
  };
  // Weekly CMG range verdict for one therapy week. `today` lets the backend decide how much of
  // the week counts as settled.
  getCmgStatus = (payload: any) => {
    return this.axiosInstance.get(`provider/pdpm-case-mix-template/cmg-status`, {
      params: {
        patientId: payload?.patientId,
        startDate: payload?.params?.startDate,
        endDate: payload?.params?.endDate,
        today: payload?.params?.today,
      },
      cancelToken: payload?.abortToken,
    });
  };

  getPdpmFeatureToggle = (payload: any) => {
    return this.axiosInstance.get(
      `provider/pdpm-case-mix-template/is-pdpm-patient?patientId=${payload?.patientId}`
    );
  };

  getCmgResolution = (payload: any) => {
    return this.axiosInstance.get(`provider/pdpm-case-mix-template/cmg-resolution`, {
      params: { patientId: payload?.patientId },
    });
  };

  getTherapyPlanningPatient = (payload: any) => {
    return this.axiosInstance.get(`provider/patient/v2/all/therapy-plan`, {
      params: payload?.params,
    });
  };

  updateTherapyPlanning = (payload: any) => {
    return this.axiosInstance.put(
      `${therapyPlanningPaths.therapyPlanning}/${payload?.patientId}?providerId=${payload?.providerId}`,
      payload?.data
    );
  };

  updateTherapyPlanningDischargeDate = (payload: any) => {
    const { patientId, providerId, ...restPayload } = payload;

    return this.axiosInstance.put(
      `${therapyPlanningPaths.therapyPlanning}set-discharge-date/${patientId}?providerId=${providerId}`,
      restPayload
    );
  };

  getMDSAassessment = (payload: any) => {
    return this.axiosInstance.get(`${therapyPlanningPaths.mdsAssessment}/${payload?.patientId}`);
  };
  getAassessment = (payload: any) => {
    return this.axiosInstance.get(
      `${therapyPlanningPaths.mdsAssessment}${therapyPlanningPaths.patientId}/${payload?.patientId}`
    );
  };
  getPlanOfCare = (payload: any) => {
    return this.axiosInstance.get(`${therapyPlanningPaths.planOfCare}/${payload?.patientId}`, {
      params: {
        episodeOfCareUuid: payload?.episodeOfCareUuid,
      },
    });
  };
  createAssessment = (payload: any) => {
    return this.axiosInstance.post(
      `${therapyPlanningPaths.mdsAssessment}/${payload?.patientId}/${payload?.moduleType}`,
      payload?.data
    );
  };
  updateAssessment = (payload: any) => {
    return this.axiosInstance.put(
      `${therapyPlanningPaths.mdsAssessment}/${payload?.patientId}/${payload?.moduleType}`,
      payload?.data
    );
  };

  // Verify PPS MDS assessment ARD against payer periods
  verifyMdsPps = (payload: any) => {
    return this.axiosInstance.post(
      `${therapyPlanningPaths.mdsAssessment}/${payload?.patientId}/verify-pps`,
      payload?.data
    );
  };

  getCptCodeByDiscipline = (payload: any) => {
    return this.axiosInstance.get(
      `${therapyPlanningPaths.cptCodeByDiscipline}/${payload?.patientId}`
    );
  };

  editAuthorization = (payload: any) => {
    return this.axiosInstance.put(`${therapyPlanningPaths.editAuth}`, payload);
  };

  getAuthDetails = (payload: any) => {
    return this.axiosInstance.get(`${therapyPlanningPaths.authDetails}/${payload?.patientId}`);
  };

  getMultipleMdsAssessment = (payload: any) => {
    return this.axiosInstance.get(`${therapyPlanningPaths.multipleMDS}/${payload?.patientId}`);
  };

  validateCsvMdsFile = (payload: any) => {
    return this.axiosInstance.post(therapyPlanningPaths.mdsValidate, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };
  uploadCSVTemplateMds = (payload: any) => {
    return this.axiosInstance.post(`${therapyPlanningPaths.mdsValidate}/upload`, payload);
  };

  //therapyPlanning 2.0
  getTherapyPlanningOfPatient_2_0 = (payload: any) => {
    return this.axiosInstance.get(therapyPlanningPaths.getTherapyPlaning_2_0(payload?.patientId), {
      params: payload?.params,
      cancelToken: payload?.abortToken,
    });
  };

  // THIN-8144: each discipline's open progress-note due date for the patient
  getProgressNoteDue = (patientId: string) => {
    return this.axiosInstance.get(therapyPlanningPaths.getProgressNoteDue(patientId));
  };

  getTherapyPlanningSummary = (payload: any) => {
    return this.axiosInstance.get(
      therapyPlanningPaths.getTherapyPlaningSummary(payload?.patientId),
      {
        params: payload?.params,
        cancelToken: payload?.abortToken,
      }
    );
  };

  editSingleCellOfTherapyPlanning = (payload: any) => {
    return this.axiosInstance.post(
      `${therapyPlanningPaths.editSingleCell(payload?.patientId)}`,
      payload?.params
    );
  };

  updateAppointmentEndTime = (payload: any) => {
    return this.axiosInstance.put(
      therapyPlanningPaths.updateAppointmentEndTime(payload?.providerId),
      null,
      {
        params: {
          taskUuid: payload?.taskUuid,
          time: payload?.time,
        },
      }
    );
  };

  updateTherapyPlanningUncoveredDay = (payload: any) => {
    return this.axiosInstance.put(
      therapyPlanningPaths.updateUncoveredDay(payload?.patientId),
      null,
      {
        params: payload?.params,
      }
    );
  };

  getDischargeDatesOfTherapyPlanning = (payload: any) => {
    return this.axiosInstance.get(therapyPlanningPaths.getDischarges(payload?.patientId));
  };

  copyPasteTherapyPlanning = (payload: any) => {
    return this.axiosInstance.put(therapyPlanningPaths.copyTherapyWeek(payload?.patientId), null, {
      params: payload.params,
    });
  };

  applyTherapyTemplates = (payload: any) => {
    return this.axiosInstance.put(
      therapyPlanningPaths.applyTheTemplates(payload?.patientId),
      null,
      {
        params: payload.params,
      }
    );
  };

  getAllTemplates = (payload: any) => {
    return this.axiosInstance.get(therapyPlanningPaths.getAllTherapyPlanningTemplates, {
      params: payload?.params,
    });
  };

  createNewTherapyTemplates = (payload: any) => {
    return this.axiosInstance.post(`provider/therapy-planning-template`, payload);
  };

  editTherapyTemplates = (payload: any) => {
    return this.axiosInstance.put(`provider/therapy-planning-template/${payload?.uuid}`, {
      templateName: payload?.templateName,
      content: payload?.content,
    });
  };

  deleteTherapyTemplates = (payload: any) => {
    return this.axiosInstance.delete(`provider/therapy-planning-template/${payload?.uuid}`);
  };

  getMdsAssessmentRange = (payload: any) => {
    return this.axiosInstance.get(therapyPlanningPaths.getMdsRange(payload?.patientId), {
      params: {
        ...payload?.params,
      },
    });
  };

  getPayerDayCount = (payload: any) => {
    return this.axiosInstance.get(therapyPlanningPaths.getPayerDayCount(payload?.patientId), {
      params: {
        ...payload?.params,
      },
    });
  };

  // THIN-8151 / THIN-8287: every Part A payer period of the patient, one 5-Day ARD window each
  getFiveDayArdWindow = (payload: any) => {
    return this.axiosInstance.get(therapyPlanningPaths.getFiveDayArdWindow(payload?.patientId));
  };

  getSingleMdsAssessment = (payload: any) => {
    return this.axiosInstance.get(therapyPlanningPaths.getSingleMds(payload.mdsAssessmentId));
  };

  deleteMdsAssessmentByDate = (payload: any) => {
    return this.axiosInstance.delete(therapyPlanningPaths.deleteMdsByDate(payload?.patientId), {
      params: payload?.param,
    });
  };

  deleteSingleMds = (payload: any) => {
    return this.axiosInstance.delete(therapyPlanningPaths.deleteSingleMds(payload?.mdsId));
  };

  get7DayLookBack = (payload: any) => {
    return this.axiosInstance.get(therapyPlanningPaths.get7DayLookBack(payload?.patientId), {
      params: payload?.params,
      cancelToken: payload?.abortToken,
    });
  };

  checkSplitMinInPlanner = (payload: any) => {
    return this.axiosInstance.post(therapyPlanningPaths.checkSplitMin(), payload?.data);
  };
}

const patientScreeningService = new PatientScreeningService();

Object.freeze(patientScreeningService);

export default patientScreeningService;
